import React, { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useGateValue } from '@statsig/react-bindings'
import { AppState } from 'redux/config/store'
import Banner from 'core-system/Banner'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import { onboardingActions } from 'features/Onboarding/onboardingActions'
import { EmployeeLocations } from 'shared/Router/EmployeeLocations'

const POLL_INTERVAL_MS = 1000 // Poll every seconds
const POLL_TIMEOUT_MS = 2 * 60 * 1000 // Stop polling after 2 minutes

const CardCreationBanner = React.memo(() => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isKingCounty = useGateValue('king_county')
  const { wallet } = useSelector((state: AppState) => state.employeePlatform)
  const { justCompletedOnboarding } = useSelector(
    (state: AppState) => state.onboarding
  )

  const [timedOut, setTimedOut] = useState(false)

  const hasCards = wallet?.cards?.length > 0
  const isPolling = !hasCards && !timedOut
  const shouldPoll = justCompletedOnboarding && isPolling && !isKingCounty

  const pollCards = useCallback(() => {
    dispatch(employeePlatformActions.getCards())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!shouldPoll) return

    let intervalId: NodeJS.Timeout
    let timeoutId: NodeJS.Timeout

    const stopPolling = () => {
      clearInterval(intervalId)
      clearTimeout(timeoutId)
    }

    pollCards()
    intervalId = setInterval(pollCards, POLL_INTERVAL_MS)
    timeoutId = setTimeout(() => {
      stopPolling()
      setTimedOut(true)
    }, POLL_TIMEOUT_MS)

    return stopPolling
  }, [shouldPoll, pollCards])

  const handleButtonClick = () => {
    dispatch(onboardingActions.updateJustCompletedOnboarding(false))
    navigate(EmployeeLocations.EmployeeSettings)
  }

  if (!justCompletedOnboarding || isKingCounty) {
    return null
  }

  if (timedOut) {
    return (
      <Banner
        variant='info'
        background='bannerCreditCard'
        title='Something went wrong creating your Fleet card'
        description='Please contact support at support@movewithfleet.com'
      />
    )
  }

  return (
    <Banner
      variant={isPolling ? 'info' : 'cta'}
      background='bannerCreditCard'
      title='Congratulations!'
      description={
        isPolling
          ? 'Your Fleet card is being created...'
          : 'Your Fleet card is ready.'
      }
      cta='Take me to my cards'
      ctaCallback={isPolling ? undefined : handleButtonClick}
    />
  )
})

export default CardCreationBanner
